// Symptoms Content - Entry pages for common urological symptoms
// Each symptom links to relevant condition articles in the platform

export const symptomsData = [
  {
    id: 'blood-in-urine',
    title: 'Blood in the Urine (Hematuria)',
    icon: 'droplet',
    summary: 'Blood in the urine can be visible or microscopic. While often benign, it always requires evaluation.',
    content: {
      whatItMeans: `Blood in the urine, called hematuria, occurs when red blood cells leak into urine at some point in the urinary tract. It can be:

**Gross (visible) hematuria** — urine appears pink, red, or cola-colored
**Microscopic hematuria** — blood only detected on urinalysis

Even a small amount of blood can change urine color significantly. A single episode of visible blood or persistent microscopic blood warrants evaluation.

**Important:** Blood in the urine is never considered "normal" and should always be investigated, even if it occurs only once.`,

      commonCauses: [
        { condition: 'Urinary tract infection (UTI)', description: 'Bladder or kidney infection causing inflammation', articleId: '7462be75-f05b-428a-a2e8-239c0ed1fb13' },
        { condition: 'Kidney stones', description: 'Stones irritating urinary tract lining as they pass', articleId: '10ca27f4-00b5-4229-a94a-c8a60140a855' },
        { condition: 'Enlarged prostate (BPH)', description: 'Enlarged blood vessels on prostate surface can bleed', articleId: '870b356f-c057-4b37-8404-6423c0dc96bd' },
        { condition: 'Bladder cancer', description: 'Tumors in bladder lining often cause painless bleeding', articleId: '22c65744-7c29-4afc-a055-0d6ce2015364' },
        { condition: 'Kidney cancer', description: 'Renal masses can cause blood in urine', articleId: 'f1b83765-9853-4d9b-9b28-8ebea1d7f9ec' },
        { condition: 'Vigorous exercise', description: 'Intense physical activity can cause transient hematuria', articleId: null },
        { condition: 'Recent procedures', description: 'Cystoscopy, prostate biopsy, or catheterization', articleId: null },
        { condition: 'Medications', description: 'Blood thinners can unmask underlying bleeding sources', articleId: null }
      ],

      whenSerious: `**Seek immediate evaluation if blood in urine is accompanied by:**
- Inability to urinate
- Severe pain
- Passing blood clots
- Fever or chills
- Significant flank or back pain

**Always get evaluated (even without other symptoms) for:**
- Any visible blood in urine
- Persistent microscopic hematuria
- Blood in urine over age 40 (higher cancer risk)
- Smokers or former smokers (bladder cancer risk)
- History of radiation or chemical exposure`,

      typicalTests: [
        { test: 'Urinalysis', description: 'Confirms blood, checks for infection, protein' },
        { test: 'Urine culture', description: 'Rules out urinary tract infection' },
        { test: 'CT urogram', description: 'Images kidneys, ureters, and bladder with contrast' },
        { test: 'Cystoscopy', description: 'Camera examination of bladder interior' },
        { test: 'Urine cytology', description: 'Checks for abnormal cells (cancer screening)' },
        { test: 'PSA test', description: 'Prostate screening in men if indicated' }
      ],

      relatedConditions: [
        { title: 'Blood in the Urine (Hematuria)', articleId: '9633e168-1e9f-4e75-8725-a751889f3480' },
        { title: 'Bladder Cancer (Non-Muscle Invasive)', articleId: '22c65744-7c29-4afc-a055-0d6ce2015364' },
        { title: 'Kidney Stones: Complete Guide', articleId: '10ca27f4-00b5-4229-a94a-c8a60140a855' },
        { title: 'Urinary Tract Infection (UTI)', articleId: '7462be75-f05b-428a-a2e8-239c0ed1fb13' },
        { title: 'Renal Cysts and Small Renal Masses', articleId: 'f1b83765-9853-4d9b-9b28-8ebea1d7f9ec' },
        { title: 'Benign Prostatic Hyperplasia (BPH)', articleId: '870b356f-c057-4b37-8404-6423c0dc96bd' }
      ]
    }
  },

  {
    id: 'testicular-pain',
    title: 'Testicular Pain',
    icon: 'alert-circle',
    summary: 'Testicular pain ranges from mild discomfort to severe pain requiring emergency care.',
    content: {
      whatItMeans: `Testicular pain can originate from the testicle itself, the epididymis (tube behind the testicle), or be referred from other areas like the abdomen or groin.

**Pain characteristics matter:**
- **Sudden, severe pain** — may indicate torsion (emergency)
- **Gradual onset** — more typical of infection or inflammation
- **Dull ache** — common with varicocele or chronic conditions
- **Pain with swelling** — suggests infection, hydrocele, or tumor

**Critical point:** Sudden severe testicular pain is a medical emergency until proven otherwise — testicular torsion can cause permanent damage within hours.`,

      commonCauses: [
        { condition: 'Epididymitis/Orchitis', description: 'Infection or inflammation of epididymis or testicle', articleId: '7eea06ef-ab0f-4ae9-ba4e-72c0552ce672' },
        { condition: 'Testicular torsion', description: 'Twisted testicle cutting off blood supply (EMERGENCY)', articleId: null },
        { condition: 'Varicocele', description: 'Enlarged veins causing dull ache, worse with standing', articleId: '629a6dd6-1569-4756-8783-6dbd509642dc' },
        { condition: 'Hydrocele/Spermatocele', description: 'Fluid collections usually painless but can cause discomfort', articleId: '51f67acb-90a4-44f8-9d47-48c6f9f5dd3b' },
        { condition: 'Inguinal hernia', description: 'Abdominal contents pushing into scrotum', articleId: null },
        { condition: 'Kidney stones', description: 'Pain can radiate to testicle from passing stone', articleId: '10ca27f4-00b5-4229-a94a-c8a60140a855' },
        { condition: 'Trauma', description: 'Direct injury to testicle', articleId: null },
        { condition: 'Chronic pelvic pain syndrome', description: 'Nerve-related pain without infection', articleId: 'bc34434d-653f-4d4c-be49-0c655c177d5b' }
      ],

      whenSerious: `**EMERGENCY — Seek immediate care for:**
- Sudden, severe testicular pain
- Pain with nausea or vomiting
- Testicle appears higher than normal or rotated
- Swelling developing rapidly
- Trauma to scrotum with persistent pain

**Urgent evaluation needed for:**
- Fever with testicular pain
- Pain with urinary symptoms (burning, frequency)
- New lump felt in testicle
- Pain not improving after several days`,

      typicalTests: [
        { test: 'Physical examination', description: 'Assesses testicle position, swelling, tenderness' },
        { test: 'Scrotal ultrasound', description: 'Checks blood flow (rules out torsion) and identifies masses' },
        { test: 'Urinalysis', description: 'Checks for infection' },
        { test: 'STI testing', description: 'If sexually transmitted infection suspected' },
        { test: 'Urine culture', description: 'Identifies bacteria if infection present' }
      ],

      relatedConditions: [
        { title: 'Epididymitis and Orchitis', articleId: '7eea06ef-ab0f-4ae9-ba4e-72c0552ce672' },
        { title: 'Varicocele: Diagnosis, Treatment & Fertility Impact', articleId: '629a6dd6-1569-4756-8783-6dbd509642dc' },
        { title: 'Hydrocele and Spermatocele', articleId: '51f67acb-90a4-44f8-9d47-48c6f9f5dd3b' },
        { title: 'Prostatitis: Complete Guide', articleId: 'bc34434d-653f-4d4c-be49-0c655c177d5b' },
        { title: 'Kidney Stones: Complete Guide', articleId: '10ca27f4-00b5-4229-a94a-c8a60140a855' }
      ]
    }
  },

  {
    id: 'frequent-urination',
    title: 'Frequent Urination',
    icon: 'clock',
    summary: 'Urinating more than 8 times daily or waking multiple times at night may signal an underlying condition.',
    content: {
      whatItMeans: `Frequent urination (urinary frequency) means needing to urinate more often than normal. What's considered "frequent" varies, but general guidelines:

**Daytime frequency:** More than 8 times in 24 hours
**Nocturia:** Waking 2 or more times at night to urinate

Frequency can occur with:
- **Large volumes** — drinking too much fluid, diabetes, diuretic medications
- **Small volumes** — bladder irritation, infection, or inability to empty completely
- **Urgency** — sudden compelling need (suggests overactive bladder)

The pattern and associated symptoms help identify the cause.`,

      commonCauses: [
        { condition: 'Overactive bladder (OAB)', description: 'Bladder contracts involuntarily, causing urgency and frequency', articleId: '4b96ad5c-2c14-4791-a80b-d8bd31a3c9d6' },
        { condition: 'Urinary tract infection', description: 'Infection irritates bladder, causing frequent small voids', articleId: '7462be75-f05b-428a-a2e8-239c0ed1fb13' },
        { condition: 'Enlarged prostate (BPH)', description: 'Obstruction causes incomplete emptying and frequency', articleId: '870b356f-c057-4b37-8404-6423c0dc96bd' },
        { condition: 'Diabetes', description: 'High blood sugar causes increased urine production', articleId: null },
        { condition: 'Excessive fluid intake', description: 'Including caffeine and alcohol which are diuretics', articleId: null },
        { condition: 'Interstitial cystitis', description: 'Chronic bladder inflammation causing pain and frequency', articleId: null },
        { condition: 'Diuretic medications', description: 'Blood pressure and heart medications increase urination', articleId: null },
        { condition: 'Prostatitis', description: 'Prostate inflammation can cause urinary symptoms', articleId: 'bc34434d-653f-4d4c-be49-0c655c177d5b' }
      ],

      whenSerious: `**Seek evaluation if frequent urination is accompanied by:**
- Blood in urine
- Pain or burning with urination
- Fever or chills
- Difficulty starting or maintaining stream
- Feeling of incomplete emptying
- Unintentional weight loss
- Excessive thirst (may indicate diabetes)

**When frequency significantly impacts:**
- Sleep quality (waking 3+ times nightly)
- Work or daily activities
- Quality of life`,

      typicalTests: [
        { test: 'Bladder diary', description: '3-day record of fluid intake, voiding times, and volumes' },
        { test: 'Urinalysis', description: 'Checks for infection, blood, glucose' },
        { test: 'Post-void residual', description: 'Ultrasound to check if bladder empties completely' },
        { test: 'Blood glucose', description: 'Screens for diabetes if indicated' },
        { test: 'PSA test', description: 'Prostate screening in men' },
        { test: 'Urodynamic studies', description: 'Bladder function testing if diagnosis unclear' }
      ],

      relatedConditions: [
        { title: 'Overactive Bladder (OAB)', articleId: '4b96ad5c-2c14-4791-a80b-d8bd31a3c9d6' },
        { title: 'Benign Prostatic Hyperplasia (BPH)', articleId: '870b356f-c057-4b37-8404-6423c0dc96bd' },
        { title: 'Urinary Tract Infection (UTI)', articleId: '7462be75-f05b-428a-a2e8-239c0ed1fb13' },
        { title: 'Prostatitis: Complete Guide', articleId: 'bc34434d-653f-4d4c-be49-0c655c177d5b' },
        { title: 'Urinary Incontinence', articleId: '2c669205-f1b7-4961-966a-22e1db4f72f8' },
        { title: 'Urinary Retention', articleId: '3389df5b-d448-4779-8d46-870a74cf8671' }
      ]
    }
  },

  {
    id: 'weak-urine-stream',
    title: 'Weak Urine Stream',
    icon: 'trending-down',
    summary: 'A weak or slow urine stream often indicates obstruction or weak bladder muscle function.',
    content: {
      whatItMeans: `A weak urine stream means reduced force or flow of urine during urination. Related symptoms often include:

- **Hesitancy** — difficulty starting urination
- **Intermittency** — stream stops and starts
- **Straining** — need to push to urinate
- **Prolonged voiding** — takes longer to empty bladder
- **Dribbling** — continued dripping after finishing
- **Double voiding** — need to urinate again shortly after

These are called "obstructive" or "voiding" symptoms and suggest the bladder is working harder than normal to push urine out.`,

      commonCauses: [
        { condition: 'Enlarged prostate (BPH)', description: 'Most common cause in men over 50', articleId: '870b356f-c057-4b37-8404-6423c0dc96bd' },
        { condition: 'Urethral stricture', description: 'Scar tissue narrowing the urethra', articleId: 'bd398644-5720-4c8b-8ea2-2265ea628c01' },
        { condition: 'Prostate cancer', description: 'Can cause obstruction similar to BPH', articleId: '18aa5db8-86b1-49f9-a0b3-10dc33480978' },
        { condition: 'Bladder weakness', description: 'Underactive bladder from nerve damage or aging', articleId: null },
        { condition: 'Medications', description: 'Some cold medicines and antihistamines affect bladder', articleId: null },
        { condition: 'Neurological conditions', description: 'Diabetes, stroke, or spinal problems affecting bladder nerves', articleId: null },
        { condition: 'Pelvic organ prolapse', description: 'In women, prolapse can kink the urethra', articleId: null }
      ],

      whenSerious: `**Seek immediate care if:**
- Complete inability to urinate (urinary retention)
- Severe lower abdominal pain with inability to void
- Fever with urinary symptoms

**Schedule prompt evaluation for:**
- Progressively worsening stream
- Need to strain significantly to urinate
- Feeling bladder never empties
- Recurrent urinary tract infections
- Blood in urine with weak stream`,

      typicalTests: [
        { test: 'Uroflowmetry', description: 'Measures urine flow rate objectively' },
        { test: 'Post-void residual', description: 'Checks how much urine remains after voiding' },
        { test: 'PSA test', description: 'Prostate screening in men' },
        { test: 'Digital rectal exam', description: 'Assesses prostate size in men' },
        { test: 'Urinalysis', description: 'Rules out infection' },
        { test: 'Cystoscopy', description: 'Visualizes urethra and bladder if stricture suspected' },
        { test: 'Urodynamic studies', description: 'Comprehensive bladder function testing' }
      ],

      relatedConditions: [
        { title: 'Benign Prostatic Hyperplasia (BPH)', articleId: '870b356f-c057-4b37-8404-6423c0dc96bd' },
        { title: 'Urethral Stricture', articleId: 'bd398644-5720-4c8b-8ea2-2265ea628c01' },
        { title: 'Urinary Retention', articleId: '3389df5b-d448-4779-8d46-870a74cf8671' },
        { title: 'Prostate Cancer (Screening)', articleId: '18aa5db8-86b1-49f9-a0b3-10dc33480978' },
        { title: 'Prostatitis: Complete Guide', articleId: 'bc34434d-653f-4d4c-be49-0c655c177d5b' }
      ]
    }
  },

  {
    id: 'painful-urination',
    title: 'Painful Urination (Dysuria)',
    icon: 'flame',
    summary: 'Burning or pain during urination is a common symptom usually indicating infection or inflammation.',
    content: {
      whatItMeans: `Painful urination (dysuria) refers to discomfort, burning, or stinging during urination. The pain may occur:

- **At the start of urination** — often urethral irritation
- **Throughout urination** — typical of bladder infection
- **At the end of urination** — may suggest bladder or prostate involvement

Associated symptoms like discharge, frequency, urgency, or fever help pinpoint the cause.

**In men:** Pain may localize to the tip of the penis or be felt deeper
**In women:** May be accompanied by vaginal symptoms`,

      commonCauses: [
        { condition: 'Urinary tract infection', description: 'Most common cause — bacteria irritating urinary tract', articleId: '7462be75-f05b-428a-a2e8-239c0ed1fb13' },
        { condition: 'Sexually transmitted infections', description: 'Chlamydia, gonorrhea, herpes can cause dysuria', articleId: null },
        { condition: 'Prostatitis', description: 'Prostate inflammation causes pain with urination in men', articleId: 'bc34434d-653f-4d4c-be49-0c655c177d5b' },
        { condition: 'Urethritis', description: 'Inflammation of the urethra', articleId: null },
        { condition: 'Kidney stones', description: 'Stones in lower ureter or bladder can cause dysuria', articleId: '10ca27f4-00b5-4229-a94a-c8a60140a855' },
        { condition: 'Interstitial cystitis', description: 'Chronic bladder inflammation (painful bladder syndrome)', articleId: null },
        { condition: 'Vaginal infections', description: 'In women, vaginitis can cause external burning', articleId: null },
        { condition: 'Chemical irritation', description: 'Soaps, spermicides, or hygiene products', articleId: null }
      ],

      whenSerious: `**Seek same-day evaluation for:**
- Fever with painful urination
- Severe flank or back pain
- Blood in urine
- Urethral discharge (especially in men)
- Painful urination during pregnancy

**Schedule evaluation for:**
- Symptoms lasting more than 2-3 days
- Recurrent episodes
- Pain with sexual intercourse
- New sexual partner`,

      typicalTests: [
        { test: 'Urinalysis', description: 'Checks for infection, blood, white blood cells' },
        { test: 'Urine culture', description: 'Identifies bacteria and antibiotic sensitivities' },
        { test: 'STI testing', description: 'Screens for sexually transmitted infections' },
        { test: 'Pelvic exam', description: 'In women, to check for vaginitis' },
        { test: 'Prostate exam', description: 'In men, if prostatitis suspected' },
        { test: 'Cystoscopy', description: 'If symptoms persist despite treatment' }
      ],

      relatedConditions: [
        { title: 'Urinary Tract Infection (UTI)', articleId: '7462be75-f05b-428a-a2e8-239c0ed1fb13' },
        { title: 'Prostatitis: Complete Guide', articleId: 'bc34434d-653f-4d4c-be49-0c655c177d5b' },
        { title: 'Kidney Stones: Complete Guide', articleId: '10ca27f4-00b5-4229-a94a-c8a60140a855' },
        { title: 'Epididymitis and Orchitis', articleId: '7eea06ef-ab0f-4ae9-ba4e-72c0552ce672' },
        { title: 'Urethral Stricture', articleId: 'bd398644-5720-4c8b-8ea2-2265ea628c01' }
      ]
    }
  },

  {
    id: 'pelvic-pain',
    title: 'Pelvic Pain',
    icon: 'activity',
    summary: 'Chronic or acute pelvic pain in men can originate from the prostate, bladder, or pelvic muscles.',
    content: {
      whatItMeans: `Pelvic pain refers to discomfort in the lower abdomen, between the belly button and groin. In urological contexts, it often involves:

**Location patterns:**
- **Suprapubic** (above pubic bone) — bladder-related
- **Perineal** (between scrotum and rectum) — prostate or pelvic floor
- **Penile or testicular** — referred from prostate or primary testicular issue
- **Lower back/sacral** — may be referred from pelvic structures

**Duration matters:**
- **Acute** — sudden onset, often infection or obstruction
- **Chronic** — lasting 3+ months, may be chronic prostatitis/CPPS

Chronic pelvic pain syndrome (CPPS) is one of the most common and challenging urological conditions.`,

      commonCauses: [
        { condition: 'Chronic prostatitis/CPPS', description: 'Most common cause of chronic pelvic pain in men', articleId: 'bc34434d-653f-4d4c-be49-0c655c177d5b' },
        { condition: 'Acute prostatitis', description: 'Bacterial infection of prostate — severe pain and fever', articleId: 'bc34434d-653f-4d4c-be49-0c655c177d5b' },
        { condition: 'Urinary tract infection', description: 'Bladder infection can cause suprapubic pain', articleId: '7462be75-f05b-428a-a2e8-239c0ed1fb13' },
        { condition: 'Interstitial cystitis', description: 'Chronic bladder pain syndrome', articleId: null },
        { condition: 'Epididymitis', description: 'Infection causing testicular and pelvic pain', articleId: '7eea06ef-ab0f-4ae9-ba4e-72c0552ce672' },
        { condition: 'Kidney stones', description: 'Lower ureteral stones can cause pelvic pain', articleId: '10ca27f4-00b5-4229-a94a-c8a60140a855' },
        { condition: 'Pelvic floor dysfunction', description: 'Muscle tension causing chronic pain', articleId: null },
        { condition: 'Urinary retention', description: 'Bladder distension causes suprapubic pain', articleId: '3389df5b-d448-4779-8d46-870a74cf8671' }
      ],

      whenSerious: `**Seek emergency care for:**
- Severe pain with fever and chills
- Inability to urinate with pelvic pain
- Sudden severe testicular pain
- Pain after trauma

**Urgent evaluation for:**
- Fever with pelvic pain
- Blood in urine
- Pain with urinary symptoms not responding to treatment
- New or rapidly worsening pain

**Schedule evaluation for:**
- Chronic pelvic pain affecting quality of life
- Pain with ejaculation
- Recurrent symptoms`,

      typicalTests: [
        { test: 'Urinalysis and culture', description: 'Rules out infection' },
        { test: 'Prostate exam (DRE)', description: 'Assesses prostate tenderness and size' },
        { test: 'PSA test', description: 'May be elevated with prostatitis' },
        { test: 'Pelvic ultrasound', description: 'Images bladder, prostate, and pelvic structures' },
        { test: 'CT scan', description: 'If kidney stone or other pathology suspected' },
        { test: 'Cystoscopy', description: 'Visualizes bladder if interstitial cystitis suspected' },
        { test: 'Urodynamics', description: 'Bladder function testing' }
      ],

      relatedConditions: [
        { title: 'Prostatitis: Complete Guide', articleId: 'bc34434d-653f-4d4c-be49-0c655c177d5b' },
        { title: 'Urinary Tract Infection (UTI)', articleId: '7462be75-f05b-428a-a2e8-239c0ed1fb13' },
        { title: 'Kidney Stones: Complete Guide', articleId: '10ca27f4-00b5-4229-a94a-c8a60140a855' },
        { title: 'Epididymitis and Orchitis', articleId: '7eea06ef-ab0f-4ae9-ba4e-72c0552ce672' },
        { title: 'Urinary Retention', articleId: '3389df5b-d448-4779-8d46-870a74cf8671' },
        { title: 'Benign Prostatic Hyperplasia (BPH)', articleId: '870b356f-c057-4b37-8404-6423c0dc96bd' }
      ]
    }
  },

  {
    id: 'erectile-problems',
    title: 'Erectile Problems',
    icon: 'heart',
    summary: 'Difficulty achieving or maintaining erections can result from physical, psychological, or combined factors.',
    content: {
      whatItMeans: `Erectile problems encompass difficulty getting an erection, maintaining an erection, or erections that aren't firm enough for sexual activity.

**Patterns to recognize:**
- **Occasional difficulty** — normal, not concerning
- **Persistent problems** — occurring more than 50% of the time
- **Situational** — works sometimes but not others (often psychological)
- **Complete absence** — no erections including morning erections (usually physical)

**Key insight:** Erectile dysfunction (ED) can be an early warning sign of cardiovascular disease. The same blood vessel problems affecting the heart can affect penile blood flow years earlier.`,

      commonCauses: [
        { condition: 'Vascular disease', description: 'Reduced blood flow from atherosclerosis, diabetes, hypertension', articleId: 'cf6fe53f-c9a4-4966-b84e-8552d936dcb6' },
        { condition: 'Low testosterone', description: 'Hormonal deficiency affecting libido and erections', articleId: '9fb844eb-d246-4f58-9e07-366d5d175343' },
        { condition: 'Diabetes', description: 'Damages blood vessels and nerves', articleId: 'cf6fe53f-c9a4-4966-b84e-8552d936dcb6' },
        { condition: 'Medications', description: 'Blood pressure meds, antidepressants, antihistamines', articleId: 'cf6fe53f-c9a4-4966-b84e-8552d936dcb6' },
        { condition: 'Psychological factors', description: 'Anxiety, depression, stress, relationship issues', articleId: 'cf6fe53f-c9a4-4966-b84e-8552d936dcb6' },
        { condition: "Peyronie's disease", description: 'Scar tissue causing curved, painful erections', articleId: '55b8dc87-fda3-449e-a87f-083d32444183' },
        { condition: 'Prostate surgery/radiation', description: 'Can damage nerves controlling erection', articleId: '2e075d98-f708-44bf-816c-fa5de5e88520' },
        { condition: 'Neurological conditions', description: 'Spinal cord injury, multiple sclerosis, Parkinson\'s', articleId: null }
      ],

      whenSerious: `**Seek prompt evaluation for:**
- Sudden onset ED (especially with other symptoms)
- ED accompanied by chest pain or shortness of breath
- Painful erections or prolonged erection (>4 hours = emergency)
- ED with pelvic trauma

**Schedule evaluation for:**
- Persistent erectile problems affecting quality of life
- ED with low libido
- ED after starting new medication
- ED with diabetes or heart disease (indicates vascular health concern)`,

      typicalTests: [
        { test: 'Medical history', description: 'Detailed questions about ED pattern, health, medications' },
        { test: 'Physical exam', description: 'Genital, cardiovascular, neurological examination' },
        { test: 'Blood tests', description: 'Testosterone, blood sugar, cholesterol, thyroid' },
        { test: 'Cardiovascular assessment', description: 'Blood pressure, heart evaluation if indicated' },
        { test: 'Penile Doppler ultrasound', description: 'Measures blood flow to penis (specialized cases)' },
        { test: 'Nocturnal penile tumescence', description: 'Monitors erections during sleep (rarely needed)' }
      ],

      relatedConditions: [
        { title: 'Erectile Dysfunction: Complete Guide', articleId: 'cf6fe53f-c9a4-4966-b84e-8552d936dcb6' },
        { title: 'Low Testosterone (Hypogonadism)', articleId: '9fb844eb-d246-4f58-9e07-366d5d175343' },
        { title: 'Testosterone Replacement Therapy', articleId: 'b598e8ea-f7c0-4fbc-bf60-86c56a9150dc' },
        { title: "Peyronie's Disease", articleId: '55b8dc87-fda3-449e-a87f-083d32444183' },
        { title: 'Prostate Cancer Treatment Options', articleId: '2e075d98-f708-44bf-816c-fa5de5e88520' },
        { title: 'Prostatectomy vs. Radiation', articleId: 'c6a9d661-62f5-448e-8d69-d477ad16d6da' }
      ]
    }
  },

  {
    id: 'flank-pain',
    title: 'Flank Pain',
    icon: 'zap',
    summary: 'Pain in the side or back between the ribs and hip can signal kidney stones, infection, or other urologic conditions.',
    content: {
      whatItMeans: `Flank pain refers to discomfort in the area between the lower ribs and the hip on either side of the back. This region overlies the kidneys, making flank pain a common presenting symptom for kidney-related problems.

**Pain characteristics to note:**
- **Location** — one side or both, exact position
- **Quality** — sharp, dull, crampy, or colicky (waves)
- **Radiation** — does it move to groin, abdomen, or testicle?
- **Timing** — constant vs. intermittent
- **Associated symptoms** — fever, nausea, urinary changes

**Colicky flank pain** (severe, crampy, comes in waves) is classic for kidney stones. The pain occurs as the ureter spasms trying to push the stone along.

**Constant, dull flank pain** may indicate infection, obstruction, or other kidney problems.`,

      commonCauses: [
        { condition: 'Kidney stones', description: 'Most common cause — stone moving through ureter causes severe colicky pain', articleId: '10ca27f4-00b5-4229-a94a-c8a60140a855' },
        { condition: 'Kidney infection (pyelonephritis)', description: 'Bacterial infection causing flank pain with fever', articleId: '7462be75-f05b-428a-a2e8-239c0ed1fb13' },
        { condition: 'Hydronephrosis', description: 'Kidney swelling from obstruction causing dull ache', articleId: 'd7e6d442-cdee-4b79-a91c-40b103cb883a' },
        { condition: 'Ureteropelvic junction obstruction', description: 'Blockage where kidney meets ureter, often with intermittent pain', articleId: null },
        { condition: 'Kidney cyst (complicated)', description: 'Ruptured or infected cyst can cause sudden pain', articleId: 'f1b83765-9853-4d9b-9b28-8ebea1d7f9ec' },
        { condition: 'Kidney tumor', description: 'Large masses may cause dull flank discomfort', articleId: 'f1b83765-9853-4d9b-9b28-8ebea1d7f9ec' },
        { condition: 'Musculoskeletal pain', description: 'Back strain or muscle spasm mimicking kidney pain', articleId: null },
        { condition: 'Referred pain', description: 'Abdominal conditions (appendicitis, gallbladder) can cause flank pain', articleId: null }
      ],

      whenSerious: `**Seek emergency care for:**
- Severe pain not controlled with medication
- **Fever with flank pain** — kidney infection can lead to sepsis
- Inability to keep fluids down (nausea/vomiting)
- Blood in urine with severe pain
- Known kidney stone with signs of infection
- Single kidney with flank pain and decreased urination
- Pain after recent trauma

**Urgent evaluation (same day) for:**
- Moderate flank pain with urinary symptoms
- Fever (even low-grade) with flank discomfort
- History of kidney stones with new pain
- Pain not improving over 24-48 hours

**Important:** An obstructed, infected kidney is a urologic emergency requiring immediate drainage.`,

      typicalTests: [
        { test: 'Urinalysis', description: 'Checks for blood, infection, crystals' },
        { test: 'CT scan (non-contrast)', description: 'Gold standard for kidney stone detection' },
        { test: 'Ultrasound', description: 'Checks for hydronephrosis, safe in pregnancy' },
        { test: 'Blood tests', description: 'Kidney function, white blood cell count, electrolytes' },
        { test: 'Urine culture', description: 'Identifies bacteria if infection suspected' },
        { test: 'CT with contrast', description: 'If mass or other pathology suspected' }
      ],

      relatedConditions: [
        { title: 'Kidney Stones: Complete Guide', articleId: '10ca27f4-00b5-4229-a94a-c8a60140a855' },
        { title: 'Hydronephrosis: When the Kidney Swells', articleId: 'd7e6d442-cdee-4b79-a91c-40b103cb883a' },
        { title: 'Urinary Tract Infection (UTI)', articleId: '7462be75-f05b-428a-a2e8-239c0ed1fb13' },
        { title: 'Dietary Recommendations for Kidney Stone Prevention', articleId: '659b8ce7-fc18-4ee9-94ec-c27516e8b325' },
        { title: 'Renal Cysts and Small Renal Masses', articleId: 'f1b83765-9853-4d9b-9b28-8ebea1d7f9ec' }
      ]
    }
  },

  {
    id: 'urinary-leakage',
    title: 'Urinary Leakage (Incontinence)',
    icon: 'droplet',
    summary: 'Involuntary urine loss affects millions. Understanding the type of leakage helps guide effective treatment.',
    content: {
      whatItMeans: `Urinary incontinence is the involuntary loss of urine. It's more common than many people realize and is NOT a normal part of aging that you simply have to accept.

**Types of urinary leakage:**

**Stress incontinence** — Leakage with coughing, sneezing, laughing, lifting, or exercise. The "stress" refers to physical pressure on the bladder, not emotional stress.

**Urge incontinence** — Sudden, intense urge to urinate followed by involuntary leakage. Often called "overactive bladder" when urgency is the main symptom.

**Overflow incontinence** — Bladder doesn't empty completely, leading to frequent dribbling. Common in men with enlarged prostate (BPH).

**Mixed incontinence** — Combination of stress and urge symptoms. Very common, especially in women.

**Important:** Incontinence is a symptom, not a diagnosis. Identifying the underlying cause leads to effective treatment.`,

      commonCauses: [
        { condition: 'Overactive bladder (OAB)', description: 'Bladder muscle contracts unexpectedly, causing urgency and leakage', articleId: '4b96ad5c-2c14-4791-a80b-d8bd31a3c9d6' },
        { condition: 'Stress urinary incontinence', description: 'Weak pelvic floor or sphincter allows leakage with pressure', articleId: '827985a9-5f5b-4a35-972a-3d4503bfa5d0' },
        { condition: 'Enlarged prostate (BPH)', description: 'Blockage causes overflow incontinence or post-void dribbling', articleId: '870b356f-c057-4b37-8404-6423c0dc96bd' },
        { condition: 'Urinary tract infection', description: 'Infection irritates bladder, causing urgency and leakage', articleId: '7462be75-f05b-428a-a2e8-239c0ed1fb13' },
        { condition: 'Neurological conditions', description: 'MS, Parkinson\'s, stroke, spinal cord issues affect bladder control', articleId: null },
        { condition: 'Post-prostatectomy', description: 'Temporary or persistent leakage after prostate surgery', articleId: null },
        { condition: 'Pelvic organ prolapse', description: 'Dropped bladder or uterus affects urinary control', articleId: null },
        { condition: 'Medications', description: 'Diuretics, sedatives, and some blood pressure medications', articleId: null }
      ],

      whenSerious: `**Seek evaluation promptly if leakage is accompanied by:**
- Blood in urine
- Pain or burning with urination
- Fever or signs of infection
- New back pain or leg weakness
- Difficulty starting urination or weak stream
- Sudden onset of severe incontinence

**Always get evaluated for:**
- Leakage affecting daily activities or sleep
- Need to wear pads or protective garments
- Avoiding activities due to fear of leakage
- Leakage that's getting progressively worse
- New incontinence after surgery or procedure`,

      typicalTests: [
        { test: 'Urinalysis', description: 'Checks for infection, blood, glucose' },
        { test: 'Post-void residual', description: 'Ultrasound to check if bladder empties completely' },
        { test: 'Bladder diary', description: '3-day record of fluid intake, voiding, and leakage episodes' },
        { test: 'Urodynamic studies', description: 'Measures bladder pressure and function during filling/emptying' },
        { test: 'Cystoscopy', description: 'Camera exam to check bladder and urethra' },
        { test: 'PSA and prostate exam', description: 'For men, to evaluate prostate size and health' }
      ],

      relatedConditions: [
        { title: 'Understanding Urinary Incontinence: Complete Guide', articleId: '2c669205-f1b7-4961-966a-22e1db4f72f8' },
        { title: 'Overactive Bladder (OAB): Complete Guide', articleId: '4b96ad5c-2c14-4791-a80b-d8bd31a3c9d6' },
        { title: 'Stress Urinary Incontinence in Women', articleId: '827985a9-5f5b-4a35-972a-3d4503bfa5d0' },
        { title: 'Urge Urinary Incontinence in Women', articleId: 'b284ebbb-c55c-4f6f-8c9a-bc7496325d2f' },
        { title: 'Benign Prostatic Hyperplasia (BPH)', articleId: '870b356f-c057-4b37-8404-6423c0dc96bd' },
        { title: 'BPH Treatment Options', articleId: '5e8bd617-ebb1-4caa-b080-c3082f9f21e5' },
        { title: 'Incontinence Surgery Options', articleId: 'e00dd822-879e-42aa-9902-b8881722dc0f' },
        { title: 'Overactive Bladder Treatment Pathway', articleId: '5226b243-b8e2-474d-a43f-54216ee9e7dd' }
      ]
    }
  }
];

export default symptomsData;
